/**
 * MUI component `color` prop accepts a fixed set of palette names.
 * Anything else (hex, rgb, CSS named colours, theme dot-notation like "text.primary")
 * must be passed via `sx: { color }` instead.
 */
export const MUI_PALETTE_COLORS = new Set([
    "inherit",
    "action",
    "disabled",
    "error",
    "info",
    "primary",
    "secondary",
    "success",
    "warning",
]);

export function isMuiPaletteColor(color: string | undefined): boolean {
    return !!color && MUI_PALETTE_COLORS.has(color);
}

/**
 * Resolves a color string into either a MUI `color` prop value or an `sx` override.
 *
 * Usage:
 * ```tsx
 * const { muiColor, colorSx } = resolveMuiColor(component.Color);
 * <Button color={muiColor} sx={{ ...colorSx }} />
 * ```
 */
export function resolveMuiColor(color: string | undefined): {
    // typed `any` so it satisfies MUI's OverridableStringUnion color prop without explicit casting at each call site
    muiColor: any;
    colorSx: Record<string, string>;
} {
    if (!color) return { muiColor: undefined, colorSx: {} };
    if (isMuiPaletteColor(color)) return { muiColor: color, colorSx: {} };
    return { muiColor: undefined, colorSx: { color } };
}
